// ── Subcontractor Portal — Phase 1 (Execution & Clarity Layer) ──
// + P8 Command Center
// + P8 IA Cleanup: tabbed (home | jobs | opps | profile), URL-persisted.
//
// Read-only consumer surface. No new write paths. No manual events.
// No scheduling engine. No pricing leakage. opportunities only via
// getVisibleOpportunities() (P3 resolver — consumed inside child components).

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useJobsQuery } from "@/hooks/useJobsQuery";
import { useObserverMode } from "@/hooks/useObserverMode";
import { ObserverBanner } from "@/components/observerMode/ObserverBanner";
import { ObserverPicker } from "@/components/observerMode/ObserverPicker";
import { useBranding } from "@/data/BrandingContext";
import { useCurrentContractor } from "@/hooks/useCurrentContractor";
import { HoldbackPanel } from "@/components/networkExec/HoldbackPanel";
import { DisputeDrawer } from "@/components/networkExec/DisputeDrawer";
import { EnforcementModeBanner } from "@/components/networkExec/EnforcementModeBanner";
import {
  filterJobsForContractor,
  deriveJobGroup,
  deriveNextActionHint,
  derivePayoutView,
  getActivePhaseExec,
  getPortalSpec,
  PORTAL_GROUP_LABEL,
  PORTAL_PAYOUT_STATUS_LABEL,
  type PortalGroup,
} from "@/data/portal/contractorPortalSelectors";
import {
  filterContractorJobs,
  filterContractorPendingOffers,
  buildPendingOfferItems,
  buildOpportunityItems,
  deriveInlineNotices,
  resolveJobPayoutSnapshot,
  getJobCompletionDate,
  computeContractorEarnings,
  type CommandCenterItem,
} from "@/data/contractor/commandCenter";
import { getContractorMaterialsReadiness } from "@/data/contractor/contractorMaterialsReadiness";
import { getPayProfile } from "@/data/contractorPayProfile";
import { getVisibleOpportunities } from "@/data/contractor/opportunityVisibility";
import { getContractorOfferMetrics } from "@/data/contractor/offerMetrics";
import { computeScorecard } from "@/hooks/useContractorScorecards";
import {
  getContractorReliability,
} from "@/data/contractorReliability";
import { getReliabilityTierLabel } from "@/data/contractor/offerMetrics";
import type { JobSpec } from "@/data/jobSpec";
import { PhaseExecutionPanel } from "@/components/execution/PhaseExecutionPanel";
import { InstallerExecutionView } from "@/components/execution/InstallerExecutionView";
import { ContractorOfferArchive } from "@/components/contractor/ContractorOfferArchive";
import { ContractorOpportunitiesPanel } from "@/components/contractor/ContractorOpportunitiesPanel";
import { ContractorPendingOffersPanel } from "@/components/contractor/ContractorPendingOffersPanel";
import { PendingOfferReviewSheet } from "@/components/contractor/PendingOfferReviewSheet";
import { ContractorStatusPanel } from "@/components/contractor/ContractorStatusPanel";
import { AvailabilityEditor } from "@/components/contractor/AvailabilityEditor";
import { ContractorService } from "@/services/contractor.service";
import { ContractorOnboardingModal } from "@/components/contractor/ContractorOnboardingModal";
import { TodayView } from "@/components/contractor/TodayView";
import { ResumeChecklistCard } from "@/components/contractor/ResumeChecklistCard";
import { getResumableChecklists } from "@/data/contractor/resumeChecklist";
import { WeekAgenda } from "@/components/contractor/WeekAgenda";
import { ContractorEarningsCard } from "@/components/contractor/ContractorEarningsCard";
import { DailyAnchorBanner } from "@/components/contractor/DailyAnchorBanner";
import { resolveDailyAnchor } from "@/data/contractor/dailyAnchor";
import { PerformanceSection } from "@/components/contractor/PerformanceSection";
import { MyCrewsSection } from "@/components/contractor/MyCrewsSection";
import { ContractorPortalErrorBoundary } from "@/components/contractor/ContractorPortalErrorBoundary";
import { ContractorIdentityGate } from "@/components/contractor/ContractorIdentityGate";
import { useRefreshCycle } from "@/hooks/useRefreshCycle";
import { LastUpdatedIndicator } from "@/components/contractor/LastUpdatedIndicator";
import { ContractorPortalEmptyState } from "@/components/contractor/ContractorPortalEmptyState";
import { ReportProblemDialog } from "@/components/contractor/ReportProblemDialog";
import { logContractorTelemetry } from "@/data/contractor/contractorTelemetry";
import { getContractorOnboardingState } from "@/data/contractor/contractorOnboarding";
import {
  EXPECTATION_FRAMING,
  ONBOARDING_MODAL_COPY,
} from "@/data/contractor/contractorMessaging";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ReadinessBadge } from "@/components/ReadinessBadge";
import { Button } from "@/components/ui/button";
import {
  HardHat, ChevronRight, ChevronLeft, MapPin, Calendar,
  AlertTriangle, Clock, ClipboardCheck,
  DollarSign, Briefcase, Sparkles, User, Home as HomeIcon,
} from "lucide-react";
import { formatAddress } from "@/lib/addressUtils";
import type { Job } from "@/data/types";

const GROUP_ORDER: PortalGroup[] = ["action_required", "in_progress", "scheduled", "complete"];

const TAB_IDS = ["home", "jobs", "opps", "profile"] as const;
type TabId = (typeof TAB_IDS)[number];
const DEFAULT_TAB: TabId = "home";

function isTabId(v: string | null): v is TabId {
  return v != null && (TAB_IDS as readonly string[]).includes(v);
}

/* ── tiny presentational helpers ─────────────────────────────────────── */

function GroupBadge({ group }: { group: PortalGroup }) {
  const cls: Record<PortalGroup, string> = {
    action_required: "bg-destructive/10 text-destructive border-destructive/20",
    in_progress: "bg-warning/10 text-warning border-warning/20",
    scheduled: "bg-info/10 text-info border-info/20",
    complete: "bg-success/10 text-success border-success/20",
  };
  return (
    <Badge variant="outline" className={`text-[10px] ${cls[group]}`}>
      {PORTAL_GROUP_LABEL[group]}
    </Badge>
  );
}

/* ── Active Jobs List ───────────────────────────────────────────────── */

function JobCard({
  job,
  group,
  hint,
  onOpen,
  profilePayout,
}: {
  job: Job;
  group: PortalGroup;
  hint: string;
  onOpen: () => void;
  profilePayout: number;
}) {
  const matRead = getContractorMaterialsReadiness(job);
  const payout = profilePayout > 0 ? profilePayout : (job.payoutAmount ?? 0);
  const matCls: Record<typeof matRead.readiness, string> = {
    installed: "bg-success/10 text-success border-success/20",
    ready: "bg-success/10 text-success border-success/20",
    partial: "bg-warning/10 text-warning border-warning/20",
    pending: "bg-muted text-muted-foreground border-border",
    unknown: "bg-muted text-muted-foreground border-border",
  };
  const mapHref = `https://maps.google.com/?q=${encodeURIComponent(formatAddress(job.address) || "")}`;
  return (
    <button
      type="button"
      data-testid="contractor-job-card"
      onClick={onOpen}
      className={`w-full text-left rounded-xl border bg-card p-3.5 active:scale-[0.99] transition-all hover:border-primary/40 ${
        group === "action_required" ? "border-destructive/40" : "border-border"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <GroupBadge group={group} />
            <Badge variant="outline" className={`text-[10px] ${matCls[matRead.readiness]}`}>
              {matRead.label}
            </Badge>
          </div>
          <p className="text-sm font-semibold truncate">{job.customerName}</p>
          <a
            href={mapHref}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-[11px] text-muted-foreground hover:text-primary flex items-center gap-1 mt-0.5 truncate"
          >
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{formatAddress(job.address) || "Address TBD"}</span>
          </a>
          <p className="text-[11px] text-foreground/80 mt-1 truncate">
            {job.projectType}
            {job.scheduledInstallDate ? ` · ${job.scheduledInstallDate}` : " · Schedule needed"}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-bold">${payout.toLocaleString()}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">payout</p>
        </div>
      </div>
      <div className="mt-2.5 pt-2.5 border-t border-border/60 flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-foreground/90 leading-snug">{hint}</p>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </div>
    </button>
  );
}

/* ── Job Execution View — delegates to canonical InstallerExecutionView ── */
// P12.2: single source of truth lives in
// src/components/execution/InstallerExecutionView.tsx. ContractorPortal's
// in-page job tap and the standalone /installer-execution/:jobId route both
// render the exact same component.

function PayoutStatusBadge({ status }: { status: keyof typeof PORTAL_PAYOUT_STATUS_LABEL }) {
  const cls: Record<typeof status, string> = {
    pending_qa: "bg-muted text-muted-foreground border-border",
    approved: "bg-info/10 text-info border-info/20",
    paid: "bg-success/10 text-success border-success/20",
  };
  return (
    <Badge variant="outline" className={`text-[10px] ${cls[status]}`}>
      {PORTAL_PAYOUT_STATUS_LABEL[status]}
    </Badge>
  );
}

/* ── Tiny status line (Home only) ───────────────────────────────────── */

function TinyStatusLine() {
  const { contractors, jobs, opportunities, issues, credits, marketplaceOffers } = useAppData();
  const { id, matchKey } = useCurrentContractor();
  const contractor = useMemo(
    () =>
      contractors.find(
        (c) => c.id === id || c.companyName.toLowerCase() === (matchKey || "").toLowerCase(),
      ) ?? null,
    [contractors, id, matchKey],
  );

  if (!contractor) return null;

  const scorecard = computeScorecard(
    contractor,
    opportunities,
    jobs,
    issues,
    credits,
    marketplaceOffers,
  );
  const reliability = getContractorReliability(scorecard);
  const tierLabel = getReliabilityTierLabel(reliability.tier);

  return (
    <p
      data-testid="tiny-status-line"
      className="text-[11px] text-muted-foreground px-1"
    >
      <span className="font-semibold text-foreground">{tierLabel}</span>
      <span className="mx-1.5">·</span>
      <span>good standing</span>
    </p>
  );
}

/* ── Attention Bridge (Home only) ───────────────────────────────────── */

function AttentionBridge({
  pendingOfferCount,
  visibleOpportunityCount,
  actionRequiredCount,
  onGo,
  onOpenPendingOffer,
}: {
  pendingOfferCount: number;
  visibleOpportunityCount: number;
  actionRequiredCount: number;
  onGo: (tab: TabId) => void;
  onOpenPendingOffer?: () => void;
}) {
  const items: { key: string; label: string; tab: TabId; tone: string; testId: string }[] = [];
  if (pendingOfferCount > 0) {
    items.push({
      key: "pending",
      label: `${pendingOfferCount} pending offer${pendingOfferCount === 1 ? "" : "s"} awaiting response`,
      tab: "opps",
      tone: "border-primary/30 bg-primary/10 text-primary",
      testId: "attention-pending-offers",
    });
  }
  if (visibleOpportunityCount > 0) {
    items.push({
      key: "opps",
      label: `${visibleOpportunityCount} opportunit${visibleOpportunityCount === 1 ? "y" : "ies"} available`,
      tab: "opps",
      tone: "border-success/30 bg-success/10 text-success",
      testId: "attention-opportunities",
    });
  }
  if (actionRequiredCount > 0) {
    items.push({
      key: "action",
      label: `${actionRequiredCount} job${actionRequiredCount === 1 ? "" : "s"} ${actionRequiredCount === 1 ? "needs" : "need"} action`,
      tab: "jobs",
      tone: "border-destructive/30 bg-destructive/10 text-destructive",
      testId: "attention-jobs-action",
    });
  }
  if (items.length === 0) return null;
  return (
    <ul data-testid="attention-bridge" className="space-y-1.5">
      {items.map((it) => (
        <li key={it.key}>
          <button
            type="button"
            data-testid={it.testId}
            data-target-tab={it.tab}
            onClick={() => {
              if (it.key === "pending" && onOpenPendingOffer) {
                onOpenPendingOffer();
                return;
              }
              onGo(it.tab);
            }}
            className={`w-full flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-[11px] font-medium ${it.tone}`}
          >
            <span>{it.label}</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ul>
  );
}

/* ── Header earnings chip — lightweight, always visible ──────────────── */

function HeaderEarningsChip() {
  const { jobs, marketplaceOffers } = useAppData();
  const { id: contractorId, matchKey } = useCurrentContractor();
  const myJobs = useMemo(
    () => filterContractorJobs(jobs, contractorId, matchKey),
    [jobs, contractorId, matchKey],
  );
  const earnings = useMemo(
    () => computeContractorEarnings(myJobs, marketplaceOffers),
    [myJobs, marketplaceOffers],
  );
  return (
    <span
      data-testid="header-earnings-chip"
      className="inline-flex items-center gap-1 rounded-full bg-success/10 text-success border border-success/20 px-2 py-0.5 text-[10px] font-semibold"
    >
      <DollarSign className="h-3 w-3" />
      ${earnings.weekRealized.toLocaleString()}
      <span className="text-[9px] text-success/70 font-normal ml-0.5">wk</span>
    </span>
  );
}

/* ── Page shell ─────────────────────────────────────────────────────── */

function ContractorPortalInner() {
  const {
    jobs,
    setJobs,
    specExecutions,
    materialKits,
    contractors,
    marketplaceOffers,
    opportunities,
    issues,
    credits,
    payProfiles,
    systemEvents,
    markContractorOnboardingComplete,
  } = useAppData();

  const { jobs: dbJobs, loading: jobsLoading } = useJobsQuery({ view: "all", search: "" });

  useEffect(() => {
    if (!jobsLoading && dbJobs.length > 0) {
      setJobs(dbJobs);
    }
  }, [dbJobs, jobsLoading, setJobs]);
  const { branding } = useBranding();
  const { id: contractorId, matchKey, displayName, isObserved } = useCurrentContractor();
  const observer = useObserverMode("contractor");
  const isObserverMode = observer.isObserverMode && isObserved === true;
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  // Tab routing — URL-persisted via ?tab=
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get("tab");
  const activeTab: TabId = isTabId(rawTab) ? rawTab : DEFAULT_TAB;
  const setActiveTab = (tab: TabId) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    next.delete("offerId");
    setSearchParams(next);
  };

  // Phase A — Pending Offer Review Sheet (overlay opened via ?offerId=…)
  const reviewOfferId = searchParams.get("offerId");
  const openOfferReview = (offerId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "opps");
    next.set("offerId", offerId);
    setSearchParams(next);
  };
  const closeOfferReview = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("offerId");
    setSearchParams(next);
  };

  const currentContractor = useMemo(() => {
    if (!matchKey) return null;
    return (
      contractors.find(
        (c) =>
          c.id === matchKey ||
          c.companyName.toLowerCase() === matchKey.toLowerCase(),
      ) ?? null
    );
  }, [contractors, matchKey]);

  const onboardingState = useMemo(() => {
    if (!currentContractor) return { shouldShow: false, reason: "completed" as const };
    return getContractorOnboardingState(currentContractor, marketplaceOffers, jobs);
  }, [currentContractor, marketplaceOffers, jobs]);

  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    if (onboardingState.shouldShow) setOnboardingOpen(true);
  }, [onboardingState.shouldShow]);

  const handleCompleteOnboarding = () => {
    if (currentContractor) markContractorOnboardingComplete(currentContractor.id);
  };

  const myJobs = useMemo(
    () => filterJobsForContractor(jobs, matchKey),
    [jobs, matchKey],
  );

  const grouped = useMemo(() => {
    const out: Record<PortalGroup, Job[]> = {
      action_required: [],
      in_progress: [],
      scheduled: [],
      complete: [],
    };
    for (const job of myJobs) {
      const exec = specExecutions.find((e) => e.jobId === job.id);
      const kit = materialKits.find((k) => k.jobId === job.id);
      const g = deriveJobGroup({ job, execution: exec, kit });
      out[g].push(job);
    }
    return out;
  }, [myJobs, specExecutions, materialKits]);

  // Contractor's pay profile payout — shown on job cards instead of computed total.
  const profilePayout = useMemo(() => {
    if (!currentContractor) return 0;
    const profile = getPayProfile(payProfiles, currentContractor.id);
    return profile?.totalPayoutAmount ?? 0;
  }, [currentContractor, payProfiles]);

  const handleSaveAvailability = async (state: import("@/data/contractor/availability").AvailabilityState) => {
    if (!contractorId) return;
    await ContractorService.updateAvailability(contractorId, state, {
      id: contractorId,
      role: "installer",
    });
  };

  // Command-center derivations for Home (calendar items + bridge counts).
  const myCmdJobs = useMemo(
    () => filterContractorJobs(jobs, contractorId, matchKey),
    [jobs, contractorId, matchKey],
  );
  const myPendingOffers = useMemo(
    () => filterContractorPendingOffers(marketplaceOffers, contractorId),
    [marketplaceOffers, contractorId],
  );
  const { tick: refreshTick, lastUpdatedAt, refresh: refreshCycle } = useRefreshCycle();
  const dailyAnchor = useMemo(
    () =>
      resolveDailyAnchor(contractorId, {
        contractors,
        jobs,
        offers: marketplaceOffers,
        now: new Date(),
      }),
    // refreshTick is intentional — drives 60s + focus + manual re-resolution
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contractorId, contractors, jobs, marketplaceOffers, refreshTick],
  );
  const visibleOppJobIds = useMemo(() => {
    if (!currentContractor) return [] as string[];
    const metrics = getContractorOfferMetrics(
      currentContractor.id,
      marketplaceOffers,
      jobs,
      systemEvents,
    );
    const scorecard = computeScorecard(
      currentContractor,
      opportunities,
      jobs,
      issues,
      credits,
      marketplaceOffers,
    );
    const resolution = getVisibleOpportunities(
      currentContractor,
      jobs,
      marketplaceOffers,
      metrics,
      scorecard,
      { payProfiles },
    );
    return resolution.opportunities.map((o) => o.jobId);
  }, [
    currentContractor,
    jobs,
    marketplaceOffers,
    opportunities,
    issues,
    credits,
    payProfiles,
    systemEvents,
  ]);

  const calendarItems = useMemo<CommandCenterItem[]>(() => {
    const lookup = new Map(jobs.map((j) => [j.id, j]));
    const todayIso = new Date().toISOString().slice(0, 10);
    const jobItems: CommandCenterItem[] = myCmdJobs
      .filter((j) => !getJobCompletionDate(j))
      .map((job) => {
        const payout = resolveJobPayoutSnapshot(job, marketplaceOffers);
        if (job.scheduledInstallDate) {
          return {
            kind: "assigned_scheduled" as const,
            jobId: job.id,
            jobLabel: String(job.jobNumber || job.id.slice(0, 8)),
            date: job.scheduledInstallDate,
            title: job.customerName,
            city: job.address?.city,
            payoutAmount: payout,
            statusLabel:
              job.scheduledInstallDate <= todayIso ? "Scheduled" : "Upcoming",
          };
        }
        return {
          kind: "assigned_unscheduled" as const,
          jobId: job.id,
          jobLabel: String(job.jobNumber || job.id.slice(0, 8)),
          date: null,
          title: job.customerName,
          city: job.address?.city,
          payoutAmount: payout,
          statusLabel: "Schedule needed",
        };
      });
    const offerItems = buildPendingOfferItems(myPendingOffers, lookup);
    const oppItems = buildOpportunityItems(visibleOppJobIds, lookup);
    return [...jobItems, ...offerItems, ...oppItems];
  }, [myCmdJobs, myPendingOffers, marketplaceOffers, jobs, visibleOppJobIds]);

  const inlineNotices = useMemo(
    () => deriveInlineNotices({ pendingOffers: myPendingOffers, assignedJobs: myCmdJobs }),
    [myPendingOffers, myCmdJobs],
  );

  // P11b — unified empty state: no assigned jobs, no pending offers, no opps.
  const isFullyEmpty =
    myCmdJobs.length === 0 &&
    myPendingOffers.length === 0 &&
    visibleOppJobIds.length === 0;

  // P11b — telemetry: anchor view (per anchor.tone change), tab open
  // P12.2 — tag observer-mode telemetry so it's excluded from real metrics.
  useEffect(() => {
    if (!contractorId) return;
    logContractorTelemetry("anchor_view", contractorId, { tone: dailyAnchor.tone }, { observer: isObserverMode });
  }, [contractorId, dailyAnchor.tone, isObserverMode]);
  useEffect(() => {
    if (!contractorId) return;
    logContractorTelemetry("tab_open", contractorId, { tab: activeTab }, { observer: isObserverMode });
  }, [contractorId, activeTab, isObserverMode]);

  const handleManualRefresh = () => {
    refreshCycle();
    if (contractorId) logContractorTelemetry("manual_refresh", contractorId, { tab: activeTab }, { observer: isObserverMode });
  };

  // Phase A — open offer review by jobId (resolves to the contractor's
  // pending offer for that job). Used by Home agenda taps.
  const openOfferReviewByJobId = (jobId: string) => {
    const o = myPendingOffers.find((x) => x.jobId === jobId);
    if (o) openOfferReview(o.id);
  };
  const openFirstPendingOfferReview = () => {
    const firstOffer = myPendingOffers[0];
    if (firstOffer) openOfferReview(firstOffer.id);
  };

  const selectedJob = selectedJobId ? myJobs.find((j) => j.id === selectedJobId) : null;
  const selectedSpec = selectedJob ? getPortalSpec(selectedJob) : undefined;

  const actionCount = grouped.action_required.length;

  return (
    <div className="min-h-screen bg-background">
      {(isObserverMode || observer.isSuperAdmin) && (
        <ObserverBanner
          displayName={displayName}
          onExit={observer.exit}
          pilotSwitchOnly={!isObserverMode}
          currentKind="contractor"
          currentId={observer.observedId ?? ""}
          contractorOptions={contractors.map((c) => ({ id: c.id, label: c.companyName }))}
          crewOptions={Array.from(
            new Set(
              jobs
                .filter((j) => j.assignmentType === "Internal Crew" && j.assignedCrew)
                .map((j) => j.assignedCrew as string),
            ),
          )
            .sort()
            .map((name) => ({ id: name, label: name }))}
          isActingAs={observer.isActingAs}
          actAsRemainingMs={observer.actAsRemainingMs}
          onStartActAs={observer.startActAs}
          onEndActAs={observer.endActAs}
        />
      )}
      <fieldset
        data-testid="observer-lockdown"
        data-observer-mode={isObserverMode ? "true" : "false"}
        disabled={isObserverMode && !observer.isActingAs}
        className="contents"
      >
      <header className="sticky top-0 z-50 bg-gradient-to-b from-card to-card/90 border-b border-border/60">
        <div className="max-w-md mx-auto px-4 py-3 flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shrink-0 ring-1 ring-primary/20">
              <span className="text-[11px] font-bold text-primary-foreground">
                {(displayName || branding.companyName)
                  .split(/\s+/)
                  .slice(0, 2)
                  .map((w) => w[0])
                  .join("")
                  .toUpperCase()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <h1
                data-testid="contractor-header-name"
                className="text-sm font-bold leading-tight line-clamp-2 break-words"
              >
                {branding.companyName}
              </h1>
              <p className="text-[10px] text-muted-foreground truncate mt-0.5">{displayName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 pt-0.5">
            <HeaderEarningsChip />
            {actionCount > 0 && !selectedJob && activeTab !== "jobs" && (
              <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/20">
                {actionCount}
              </Badge>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-4 pb-36">
        <EnforcementModeBanner />
        {selectedJob && selectedSpec ? (
          <InstallerExecutionView
            job={selectedJob}
            spec={selectedSpec}
            onBack={() => setSelectedJobId(null)}
          />
        ) : selectedJob && !selectedSpec ? (
          <Card>
            <CardContent className="p-6 text-center space-y-2">
              {selectedJob.currentStage === "Completed" ? (
                <>
                  <ClipboardCheck className="h-8 w-8 text-success mx-auto" />
                  <p className="text-sm font-semibold">Job completed</p>
                  <p className="text-xs text-muted-foreground">
                    This completed job has no active execution spec session.
                  </p>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-8 w-8 text-warning mx-auto" />
                  <p className="text-sm font-semibold">No JobSpec bound</p>
                  <p className="text-xs text-muted-foreground">
                    This job has no execution spec attached. Contact your PM.
                  </p>
                </>
              )}
              <Button variant="outline" size="sm" onClick={() => setSelectedJobId(null)}>
                Back
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div data-testid="contractor-portal-tabs" data-active-tab={activeTab}>
            {activeTab === "home" && (
              <section data-testid="tab-panel-home" className="space-y-4">
                <DailyAnchorBanner anchor={dailyAnchor} />
                <LastUpdatedIndicator
                  lastUpdatedAt={lastUpdatedAt}
                  onRefresh={handleManualRefresh}
                />
                {isFullyEmpty ? (
                  <ContractorPortalEmptyState />
                ) : (
                  <>
                    <TinyStatusLine />
                    <TodayView onOpenPendingOffer={openFirstPendingOfferReview} />
                    <AttentionBridge
                      pendingOfferCount={myPendingOffers.length}
                      visibleOpportunityCount={visibleOppJobIds.length}
                      actionRequiredCount={actionCount}
                      onGo={setActiveTab}
                      onOpenPendingOffer={openFirstPendingOfferReview}
                    />
                    {inlineNotices.length > 0 && (
                      <ul data-testid="inline-notices" className="space-y-1.5">
                        {inlineNotices.map((n) => (
                          <li
                            key={n.id}
                            data-tone={n.tone}
                            className={`flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-[11px] ${
                              n.tone === "warning"
                                ? "border-warning/30 bg-warning/10 text-warning"
                                : "border-info/30 bg-info/10 text-info"
                            }`}
                          >
                            <Clock className="h-3 w-3 mt-0.5 shrink-0" />
                            <span>{n.message}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Card>
                      <CardContent className="p-4">
                        <WeekAgenda
                          items={calendarItems}
                          onItemClick={(it) => {
                            if (it.kind === "pending_offer") {
                              openOfferReviewByJobId(it.jobId);
                            }
                          }}
                        />
                      </CardContent>
                    </Card>
                    <ContractorEarningsCard />
                  </>
                )}
              </section>
            )}

            {activeTab === "jobs" && (
              <section data-testid="tab-panel-jobs" className="space-y-3">
                <ResumeChecklistCard
                  items={getResumableChecklists({
                    jobs,
                    specExecutions,
                    matchKey,
                    todayIso: new Date().toISOString().slice(0, 10),
                  })}
                />
                <ActiveJobsList
                  grouped={grouped}
                  specExecutions={specExecutions}
                  materialKits={materialKits}
                  onOpen={(id) => setSelectedJobId(id)}
                  totalCount={myJobs.length}
                  profilePayout={profilePayout}
                />
              </section>
            )}

            {activeTab === "opps" && (
              <section data-testid="tab-panel-opps" className="space-y-5">
                <ContractorPendingOffersPanel
                  focusJobId={searchParams.get("jobId")}
                  onOpenReview={openOfferReview}
                />
                <ContractorOpportunitiesPanel />
                <ContractorOfferArchive />
              </section>
            )}

            {activeTab === "profile" && (
              <section data-testid="tab-panel-profile" className="space-y-4">
                {currentContractor && (
                  <AvailabilityEditor
                    contractor={currentContractor}
                    editable
                    allowedModes={["available", "limited", "pto", "paused"]}
                    onAfterSave={handleSaveAvailability}
                  />
                )}
                <ContractorStatusPanel />
                <PerformanceSection />
                <MyCrewsSection />
                <HoldbackPanel contractorId={contractorId} readOnly />
                <DisputeDrawer contractorId={contractorId} readOnly />
                <p
                  data-testid="expectation-framing"
                  className="text-[11px] text-muted-foreground px-1"
                >
                  {EXPECTATION_FRAMING.curated} {EXPECTATION_FRAMING.matching}
                </p>
                <button
                  type="button"
                  onClick={() => setHelpOpen(true)}
                  className="text-[11px] font-semibold text-primary hover:underline"
                  data-testid="how-koredor-works-link"
                >
                  {ONBOARDING_MODAL_COPY.persistentLink}
                </button>
                <div className="pt-2">
                  <button
                    type="button"
                    data-testid="report-problem-button"
                    onClick={() => setReportOpen(true)}
                    className="w-full min-h-[44px] rounded-md border border-border bg-card px-3 py-2 text-xs font-medium text-foreground hover:bg-muted"
                  >
                    Report a problem
                  </button>
                </div>
              </section>
            )}
          </div>
        )}
      </main>


      </fieldset>

      {/* Bottom tab bar — mobile-first.
          Intentionally OUTSIDE the observer-lockdown fieldset so SuperAdmin
          observers can still navigate between Home/Jobs/Opps/Profile while
          the surface itself remains read-only. */}
      {!selectedJob && (
        <>
          {/* Solid backdrop under the floating bar so scrolled content
              visually stops above it instead of bleeding through. */}
          <div
            aria-hidden
            className="fixed inset-x-0 md:left-[var(--app-shell-sidebar-width)] bottom-0 z-30 pointer-events-none bg-background"
            style={{ height: "calc(env(safe-area-inset-bottom, 0px) + 5rem)" }}
          />
          {/* Offset by the AppLayout sidebar width on md+ so the bar centers
              within the content column, not the entire viewport. */}
          <div className="fixed inset-x-0 md:left-[var(--app-shell-sidebar-width)] bottom-0 z-40 pointer-events-none">
            <div className="mx-auto w-full max-w-md px-4 pb-[max(env(safe-area-inset-bottom),0.75rem)]">

              <nav
                data-testid="contractor-tab-bar"
                className="pointer-events-auto bg-card/95 backdrop-blur border border-border/60 rounded-xl shadow-lg"
              >
                <div className="grid grid-cols-4">
                  <TabButton id="home" label="Home" icon={HomeIcon} active={activeTab === "home"} onClick={setActiveTab} />
                  <TabButton id="jobs" label="Jobs" icon={Briefcase} active={activeTab === "jobs"} onClick={setActiveTab} badge={actionCount > 0 ? actionCount : undefined} />
                  <TabButton id="opps" label="Opportunities" icon={Sparkles} active={activeTab === "opps"} onClick={setActiveTab} badge={(myPendingOffers.length + visibleOppJobIds.length) || undefined} />
                  <TabButton id="profile" label="Profile" icon={User} active={activeTab === "profile"} onClick={setActiveTab} />
                </div>
              </nav>
            </div>
          </div>
        </>
      )}

      <ContractorOnboardingModal
        open={onboardingOpen}
        onOpenChange={setOnboardingOpen}
        onComplete={handleCompleteOnboarding}
      />
      <ContractorOnboardingModal
        open={helpOpen}
        onOpenChange={setHelpOpen}
        onComplete={() => {}}
        singlePage
      />
      <ReportProblemDialog
        contractorId={contractorId}
        currentTab={activeTab}
        open={reportOpen}
        onOpenChange={setReportOpen}
      />
      <PendingOfferReviewSheet
        offerId={reviewOfferId}
        onOpenChange={(o) => {
          if (!o) closeOfferReview();
        }}
        disableActions={isObserverMode && !observer.isActingAs}
      />
    </div>
  );
}

function TabButton({
  id,
  label,
  icon: Icon,
  active,
  badge,
  onClick,
}: {
  id: TabId;
  label: string;
  icon: typeof HomeIcon;
  active: boolean;
  badge?: number;
  onClick: (id: TabId) => void;
}) {
  return (
    <button
      type="button"
      data-testid={`tab-${id}`}
      data-active={active}
      aria-pressed={active}
      onClick={() => onClick(id)}
      className={`relative flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors ${
        active
          ? "text-primary bg-primary/10"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
      {badge != null && badge > 0 && (
        <span className="absolute top-1.5 right-1/2 translate-x-[14px] inline-flex items-center justify-center h-3.5 min-w-[14px] px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold">
          {badge}
        </span>
      )}
    </button>
  );
}

function ActiveJobsList({
  grouped,
  specExecutions,
  materialKits,
  onOpen,
  totalCount,
  profilePayout,
}: {
  grouped: Record<PortalGroup, Job[]>;
  specExecutions: ReturnType<typeof useAppData>["specExecutions"];
  materialKits: ReturnType<typeof useAppData>["materialKits"];
  onOpen: (jobId: string) => void;
  totalCount: number;
  profilePayout: number;
}) {
  if (totalCount === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center space-y-2">
          <Clock className="h-8 w-8 text-muted-foreground mx-auto" />
          <p className="text-sm font-medium">No assigned jobs</p>
          <p className="text-xs text-muted-foreground">New jobs will appear here once assigned.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {GROUP_ORDER.map((group) => {
        const list = grouped[group];
        if (list.length === 0) return null;
        return (
          <section key={group}>
            <div className="flex items-center justify-between mb-2 px-0.5">
              <h2 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                {PORTAL_GROUP_LABEL[group]}
              </h2>
              <span className="text-[10px] text-muted-foreground">{list.length}</span>
            </div>
            <div className="space-y-2">
              {list.map((job) => {
                const exec = specExecutions.find((e) => e.jobId === job.id);
                const kit = materialKits.find((k) => k.jobId === job.id);
                const hint = deriveNextActionHint({ job, execution: exec, kit });
                return (
                  <JobCard
                    key={job.id}
                    job={job}
                    group={group}
                    hint={hint}
                    onOpen={() => onOpen(job.id)}
                    profilePayout={profilePayout}
                  />
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/* ── Default export: wrap inner with identity gate + error boundary ── */

function ContractorPortalGate() {
  const { contractors } = useAppData();
  const observer = useObserverMode("contractor");
  const { isUnresolved, refresh } = useCurrentContractor();

  // SuperAdmin path: when there's no canonical session and no observeAs
  // target chosen, surface the picker. If a real contractor session exists
  // (e.g., devtools-seeded test fixture or actual pilot session), we leave
  // it alone — the avatar-menu Pilot Views entry is the documented entry
  // point for observation.
  if (observer.isSuperAdmin && !observer.observedId && isUnresolved) {
    return (
      <ObserverPicker
        title="Observe contractor portal"
        basePath="/contractor-portal"
        options={contractors.map((c) => ({
          id: c.id,
          label: c.companyName,
          sublabel: c.id,
        }))}
      />
    );
  }

  // Real contractor path (subcontractor role): unchanged identity gate.
  if (isUnresolved) return <ContractorIdentityGate onResolved={refresh} />;
  return <ContractorPortalInner />;
}

export default function ContractorPortal() {
  return (
    <ContractorPortalErrorBoundary>
      <ContractorPortalGate />
    </ContractorPortalErrorBoundary>
  );
}
